import { useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { Package } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { UserRole } from '@/types/enums';
import { Button, Input } from '@/components/ui';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

const loginSchema = z.object({
  identifier: z.string().min(1, 'Введите email или телефон'),
  password: z.string().min(1, 'Введите пароль'),
});

type LoginForm = z.infer<typeof loginSchema>;

const LoginPage: React.FC = () => {
  const { login, isAuthenticated, isLoading, user } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  useEffect(() => {
    if (isAuthenticated && user) {
      const defaultPath = user.role === UserRole.CUSTOMER ? '/my-orders' : '/dashboard';
      navigate(from || defaultPath, { replace: true });
    }
  }, [isAuthenticated, user, navigate, from]);

  const onSubmit = async (data: LoginForm) => {
    try {
      await login(data);
      toast.success('Вход выполнен');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Неверные данные для входа'));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-primary-600 flex items-center justify-center mx-auto mb-4 shadow-card">
            <Package className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">AFlow</h1>
          <p className="text-sm text-gray-500 mt-1">Система управления пунктом выдачи</p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Войти в систему</h2>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <Input
              label="Email или телефон"
              placeholder="Email или номер телефона"
              error={errors.identifier?.message}
              required
              {...register('identifier')}
            />

            <Input
              label="Пароль"
              type="password"
              placeholder="••••••••"
              error={errors.password?.message}
              required
              {...register('password')}
            />

            <Button
              type="submit"
              loading={isSubmitting || isLoading}
              className="w-full"
            >
              Войти
            </Button>
          </form>
        </div>

        <div className="text-center mt-6 space-y-2">
          <Link to="/register" className="text-sm text-primary-600 hover:text-primary-700 transition-colors">
            Зарегистрироваться как клиент
          </Link>
          <br />
          <Link to="/register-branch" className="text-sm text-primary-600 hover:text-primary-700 transition-colors">
            Зарегистрировать ОЦ
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
